import { Loader2 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';

import type { DepositSource } from '../services/directDeposit';
import type { CustodialDepositState } from '../types';

/**
 * USDC below this stays where it is: a transfer costs the service wallet a HyperEVM tx and
 * the figure would not change what the user can open.
 */
export const HYPERCORE_TOPUP_MIN_USD = 0.05;
const USDC_DECIMALS = 6;

interface HyperCoreTopUpNoticeProps {
    /** USDC held by the custodial wallet on HyperEVM (display units); null while unknown. */
    heldUsd: number | null | undefined;
    depositState: CustodialDepositState;
    onTransfer: (amount: bigint, source: DepositSource) => Promise<void>;
    onDone: () => void;
}

/**
 * The user only ever sees one balance, the HyperCore one, because that is the only money a
 * fast open can use. A full deposit lands everything there; when something is left in the
 * HyperEVM custodial wallet (a funding that was deferred, a floor-bounded funding's residue),
 * this says how much and fixes it with one tap. No amount to type: it always moves all of it.
 */
const HyperCoreTopUpNotice: React.FC<HyperCoreTopUpNoticeProps> = ({
    heldUsd,
    depositState,
    onTransfer,
    onDone,
}) => {
    const held = heldUsd ?? 0;
    const running =
        depositState.status === 'submitting' ||
        depositState.status === 'awaitingSignature' ||
        depositState.status === 'bridging';
    // a run that finished (or failed) is only ours to report if it was the transfer we started;
    // a Cronos deposit that just settled must not read as a top-up outcome.
    const [ownJobId, setOwnJobId] = React.useState<string | null>(null);
    const ownRun = ownJobId !== null && depositState.jobId === ownJobId;
    const finished = ownRun && depositState.status === 'success';
    const failed = ownRun && depositState.status === 'failed';

    React.useEffect(() => {
        if (running && depositState.jobId && ownJobId === 'pending') {
            setOwnJobId(depositState.jobId);
        }
    }, [running, depositState.jobId, ownJobId]);

    if (held < HYPERCORE_TOPUP_MIN_USD && !finished && !failed) return null;

    const handleTransfer = () => {
        setOwnJobId('pending');
        const amount = BigInt(Math.floor(held * 10 ** USDC_DECIMALS));
        void onTransfer(amount, 'walletBalance');
    };
    const reset = () => {
        setOwnJobId(null);
        onDone();
    };

    if (finished) {
        return (
            <div className="flex items-center justify-between gap-3 border-b border-border bg-primary/5 px-4 py-2.5">
                <p className="text-xs text-foreground">Moved to your HyperCore account. Fast opens can use it now.</p>
                <Button variant="ghost" size="sm" onClick={reset} className="h-7 px-2 text-[11px]">
                    OK
                </Button>
            </div>
        );
    }
    if (failed) {
        return (
            <div className="flex items-center justify-between gap-3 border-b border-border bg-destructive/5 px-4 py-2.5">
                <p className="text-xs text-foreground">
                    The transfer did not go through. {depositState.error ?? 'Please try again.'}
                </p>
                <Button variant="secondary" size="sm" onClick={reset} className="h-7 px-2 text-[11px]">
                    Try again
                </Button>
            </div>
        );
    }
    return (
        <div className="flex items-center justify-between gap-3 border-b border-border bg-primary/5 px-4 py-2.5">
            <p className="text-xs text-foreground">
                ${held.toFixed(2)} available to transfer to your HyperCore account for fast opens.
            </p>
            <Button
                variant="default"
                size="sm"
                onClick={handleTransfer}
                disabled={running}
                className="h-7 shrink-0 gap-1 px-2.5 text-[11px] font-semibold"
            >
                {running && ownJobId !== null ? (
                    <>
                        <Loader2 size={12} className="animate-spin" />
                        {depositState.progressMessage ?? 'Transferring'}
                    </>
                ) : (
                    'Transfer'
                )}
            </Button>
        </div>
    );
};

export default HyperCoreTopUpNotice;
